import React, { useEffect } from "react";
//#region types
import { ChartContainerDataSet, ChartDataSet, DoughnutChartProps } from "models/propTypes/chartProps";
import { GraphConstants } from "utils/constants";
import { ChartEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import Chart, { ChartColor } from "chart.js";
import { Grid } from "@mui/material";
import moment from "moment";
import MultiLineChartTitle from "./multiLineTitle";
//#endregion
import { ChartHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import * as ChartJs from "chart.js";
import useChartContext from "./context/useContext";
import { createFreezeTooltipPlugin } from "./utils/freezeTooltipPlugin";
import { registerOutsideClickClear } from "./utils/outsideClick";

export default function DoughnutChart(props: React.PropsWithChildren<DoughnutChartProps>) {
  const chartCanvasId = `${GraphConstants.DoughnutChartIdPrefix}${props.id}`;
  const chartLegendId = `${GraphConstants.LegendIdPrefix}${GraphConstants.DoughnutChartIdPrefix}${props.id}`;

  const [dataPoints, setDataPoints] = React.useState<ChartContainerDataSet[] | undefined>(getData());
  const [legendType, setLegendType] = React.useState<ChartEnums.LegendType>(props.legendType);
  const [colorPaletteType, setColorPaletteType] = React.useState<ChartEnums.DoughnutChartColorPalette>(
    props.colorPaletteType
  );
  const [total, setTotal] = React.useState<number>(0);
  const [className, setClassName] = React.useState<string>("");
  const [middleText, setMiddleText] = React.useState<string>();

  const selectedIndexRef = React.useRef<number | null>(null);
  const frozenRef = React.useRef<boolean>(false);
  const chartRef = React.useRef<any>(null);
  const justSetSelectionRef = React.useRef<number | null>(null);

  const chartContext = useChartContext();

  const fontSize = 16;
  const cutoutPercentage = 70;

  function resetSelectionAndChart(chart: any) {
    selectedIndexRef.current = null;
    frozenRef.current = false;
    try {
      const ds = chart?.data?.datasets && chart.data.datasets[0] ? chart.data.datasets[0] : undefined;
      if (ds) {
        (ds as any).backgroundColor = ChartHelper.getDoughnutChartColorPalette(colorPaletteType, dataPoints);
      }
      (chart as any).tooltip._active = [];
      (chart as any).tooltip.update(true);
      chart.update();
    } catch {}
    try {
      chartContext?.setSelection && chartContext.setSelection(String(props.id), null);
    } catch {}
  }

  React.useEffect(() => {
    const datasets = props.datasets;

    if (frozenRef.current) {
      return;
    }

    if (datasets === undefined || datasets.length === 0 || (datasets.length > 0 && datasets[0].data === undefined)) {
      setLegendType(ChartEnums.LegendType.LARGE);
      setColorPaletteType(ChartEnums.DoughnutChartColorPalette.EMPTY);
      setClassName(cssExports.doughnutGraphEmptyContainer);
      setDataPoints([
        {
          data: 100,
          label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.MissingInformation),
        } as ChartDataSet,
      ]);
    } else {
      setLegendType(props.legendType);
      setColorPaletteType(props.colorPaletteType);
      setClassName("");
      setDataPoints(getData());
    }
  }, [props.datasets]);

  const getPlugin = (middleText: string) => {
    return {
      id: chartCanvasId,
      afterDraw: function (chart: any) {
        if (chart.canvas?.id !== chartCanvasId || middleText?.length === undefined) return;

        const ctx = chart.ctx;

        ctx?.save();
        // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
        if (ctx != undefined) {
          ctx.font = `900 ${fontSize}px MuseoSans-900`;
          ctx.fillStyle = GraphConstants.MiddleTextFontColor;
        }

        const centerX = (chart.chartArea.left + chart.chartArea.right - (middleText.length * fontSize) / 2) / 2;
        const centerY = (chart.chartArea.top + chart.chartArea.bottom + fontSize / 2) / 2;
        ctx?.fillText(middleText, centerX, centerY);
        ctx?.restore();
      },
    };
  };

  const getFreezePlugin = () =>
    createFreezeTooltipPlugin({
      id: `${chartCanvasId}-freeze`,
      canvasId: chartCanvasId,
      isFrozenRef: frozenRef,
      hasSelectionRef: selectedIndexRef as any,
      getActiveElement: (chart: any) => chart.getDatasetMeta(0)?.data?.[selectedIndexRef.current as any],
      withBeforeUpdate: false,
      withAfterDraw: false,
      drawOnAfterEvent: false,
    });

  React.useEffect(() => {
    if (middleText !== props.middleText) {
      const plugin = Chart.pluginService.getAll().find((c) => c.id === chartCanvasId);
      plugin && Chart.pluginService.unregister(plugin);
      props.middleText && Chart.pluginService.register(getPlugin(props.middleText));
      setMiddleText(props.middleText);
    }
  }, [props.middleText]);

  React.useEffect(() => {
    const freezePluginId = `${chartCanvasId}-freeze`;
    try {
      const existing = Chart.pluginService.getAll().find((c) => c.id === freezePluginId);
      existing && Chart.pluginService.unregister(existing);
    } catch {}
    try {
      Chart.pluginService.register(getFreezePlugin());
    } catch {}
    return () => {
      try {
        const existing = Chart.pluginService.getAll().find((c) => c.id === freezePluginId);
        existing && Chart.pluginService.unregister(existing);
      } catch {}
    };
  }, [chartCanvasId]);

  useEffect(() => {
    let ctx: any = document.getElementById(chartCanvasId);

    const data = dataPoints?.map((dataPoint) => {
      return dataPoint.data as number;
    });

    const colors = ChartHelper.getDoughnutChartColorPalette(colorPaletteType, dataPoints);

    const chart = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: ChartHelper.getLabelsSet(dataPoints),
        datasets: [
          {
            data: data,
            backgroundColor: colors,
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        legendCallback: function (chart) {
          return generateLegend(chart);
        },
        onHover: function (event, activeElements) {
          ctx.style.cursor = activeElements[0] ? "pointer" : "default";
        },
        onClick: function (event, activeElements) {
          if (!props.useClickableChart) return;
          const activePoints = chart.getElementsAtEvent(event);
          if (activePoints[0]) {
            const chartData = chart.config.data;
            const index = (activePoints[0] as any)._index;

            const label = chartData?.labels !== undefined ? chartData.labels[index] : undefined;

            // Freeze selection and dim other slices
            try {
              try {
                chartContext?.setSelectedChartId && chartContext.setSelectedChartId(String(props.id));
              } catch {}
              const ds = chart.data.datasets && chart.data.datasets[0] ? chart.data.datasets[0] : undefined;
              if (ds) {
                const baseColors = ChartHelper.getDoughnutChartColorPalette(colorPaletteType, dataPoints);
                const newColors = (baseColors as ChartColor[]).map((c, i) =>
                  i === index
                    ? c.toString()
                    : (Chart.helpers as any)
                        .color(c as any)
                        .alpha(0.3)
                        .rgbString()
                );
                (ds as any).backgroundColor = newColors;
              }

              frozenRef.current = true;
              selectedIndexRef.current = index;
              chartContext?.setSelection && chartContext.setSelection(String(props.id), { index });
              justSetSelectionRef.current = Date.now();

              const activeArc = chart.getDatasetMeta(0)?.data?.[index];
              if (activeArc) {
                (chart as any).tooltip._active = [activeArc];
                (chart as any).tooltip.update(true);
              }
              chart.update();

              if (props.onClick) props.onClick(index, label);
            } catch (e) {
              // no-op on styling errors
            }
          }
        },
        title: {
          display: false,
        },
        cutoutPercentage: cutoutPercentage,
        tooltips: {
          backgroundColor: GraphConstants.TooltipBackgroundColor,
          titleFontColor: GraphConstants.TooltipTitleFontColor,
          titleFontSize: GraphConstants.TooltipTitleFontSize,
          bodyFontColor: GraphConstants.TooltipBodyFontColor,
          bodyFontSize: GraphConstants.TooltipBodyFontSize,
          displayColors: false,
          callbacks: {
            title: function (items, data) {
              if (
                data !== undefined &&
                items !== undefined &&
                data["labels"] !== undefined &&
                items[0]["index"] !== undefined
              )
                return data["labels"][items[0]["index"]].toString();
              else return "";
            },
            label: function (tooltipItem, data) {
              const meta = chart.getDatasetMeta(0);
              const total = meta.total !== undefined ? Number.parseInt(meta.total ?? "") : 0;
              const index = tooltipItem.index;
              // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
              const dataset = data != undefined && data?.datasets != undefined ? data?.datasets[0] : undefined;
              const value =
                // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
                index != undefined && dataset != undefined && dataset.data != undefined
                  ? Math.abs(dataset.data[index] as number)
                  : 0;
              const perc = Math.round((100 * value) / total);
              return `${LocalizationHelperInstance.getFormattedNumber(value, 0, 0)} ${
                props.dataUnit ?? GraphConstants.DefaultDataUnit
              }, ${perc}%`;
            },
          },
        },
      },
    });

    try {
      const stored = chartContext?.getSelection ? chartContext.getSelection(String(props.id)) : null;
      const selected = stored ? stored.index : null;
      if (selected != null) {
        const ds = chart.data?.datasets && chart.data.datasets[0] ? chart.data.datasets[0] : undefined;
        if (ds) {
          const baseColors = ChartHelper.getDoughnutChartColorPalette(colorPaletteType, dataPoints);
          const newColors = (baseColors as ChartColor[]).map((c, i) =>
            i === selected
              ? c.toString()
              : (Chart.helpers as any)
                  .color(c as any)
                  .alpha(0.3)
                  .rgbString()
          );
          (ds as any).backgroundColor = newColors;
        }
        frozenRef.current = true;
        selectedIndexRef.current = selected;
        const activeArc = chart.getDatasetMeta(0)?.data?.[selected];
        if (activeArc) {
          (chart as any).tooltip._active = [activeArc];
          (chart as any).tooltip.update(true);
        }
        chart.update();
      }
    } catch {}

    if (!props.datasets?.length) {
      setTotal(0);
    } else {
      const initialTotal = props.datasets.reduce((a, b) => a + getChartDataValue(b.data), 0);
      setTotal(initialTotal);
    }

    configureLegendClick((legendItem) => legendItem.addEventListener("click", legendClickCallback, false), chart);
    chartRef.current = chart;

    return () => {
      ctx = null;
      chart.destroy();
      configureLegendClick((legendItem: HTMLLIElement) => legendItem.removeEventListener("click", legendClickCallback));
    };
  }, [dataPoints, legendType, colorPaletteType]);

  React.useEffect(() => {
    const selected = chartContext?.selectedChartId;
    // If selection is cleared globally (e.g., Clear filter), clear this chart immediately
    if (!selected) {
      if (chartRef.current && (frozenRef.current || selectedIndexRef.current != null)) {
        try {
          resetSelectionAndChart(chartRef.current);
          justSetSelectionRef.current = null;
        } catch {}
      }
      return;
    }
    if (selected === props.id) return;
    // If this chart was just clicked (grace period), ignore immediate context changes from other charts
    try {
      const now = Date.now();
      const lastSetAt = justSetSelectionRef.current;
      const withinGrace = lastSetAt != null && now - lastSetAt < 600;
      const hasStoredSelection = !!(chartContext?.getSelection && chartContext.getSelection(String(props.id)));
      if (withinGrace && hasStoredSelection && frozenRef.current && selectedIndexRef.current != null) {
        return;
      }
    } catch {}
    if (chartRef.current) {
      frozenRef.current = false;
      selectedIndexRef.current = null;
      justSetSelectionRef.current = null;
      try {
        const chart = chartRef.current as any;
        const ds = chart.data.datasets && chart.data.datasets[0] ? chart.data.datasets[0] : undefined;
        if (ds) {
          (ds as any).backgroundColor = ChartHelper.getDoughnutChartColorPalette(colorPaletteType, dataPoints);
        }
        chart.tooltip._active = [];
        chart.tooltip.update(true);
        try {
          chartContext?.setSelection && chartContext.setSelection(String(props.id), null);
        } catch {}
        chart.update();
      } catch {}
    }
  }, [chartContext?.selectedChartId, colorPaletteType, dataPoints, props.id]);

  React.useEffect(() => {
    const unregister = registerOutsideClickClear({
      canvasId: chartCanvasId,
      legendId: chartLegendId,
      resolvePanelRoot: (canvas) => {
        const container =
          canvas && (canvas as any).closest ? (canvas as any).closest(`.${cssExports.doughnutGraphContainer}`) : null;
        const outer =
          container && (container as any).closest ? (container as any).closest(`.${cssExports.viewPanelOuter}`) : null;
        const parent = (outer as HTMLElement | null) ? (outer as HTMLElement).parentElement : null;
        return parent as HTMLElement | null;
      },
      insideCanvasHandling: "background-only",
      getChart: () => chartRef.current,
      shouldSkip: () => !props.useClickableChart || (!frozenRef.current && selectedIndexRef.current == null),
      onClear: () => {
        if (chartRef.current) {
          try {
            const chart = chartRef.current as any;
            try {
              if (props.onBackgroundClick) props.onBackgroundClick();
            } catch {}
            resetSelectionAndChart(chart);
            try {
              chartContext?.setSelectedChartId && chartContext.setSelectedChartId(null);
            } catch {}
          } catch {}
        }
      },
    });
    return unregister;
  }, [chartCanvasId, chartLegendId, colorPaletteType, dataPoints, chartContext?.setSelectedChartId]);

  function getChartDataValue(data?: number | Array<number | null | undefined | number[]> | ChartJs.ChartPoint[]) {
    let value = 0;
    if (!Array.isArray(data)) {
      value = data ?? 0;
    } else {
      for (let i = 0; i < data.length; i++) {
        const dataItem = data[i];
        if (typeof dataItem === "number") {
          value += dataItem;
        } else if (Array.isArray(dataItem)) {
          value += dataItem.reduce((a, b) => a + b, 0);
        } else {
          value += (dataItem as ChartJs.ChartPoint).r ?? 0;
        }
      }
    }
    return value;
  }

  function getData() {
    if (props.datasets === undefined || (props.datasets !== undefined && props.datasets.length <= 9))
      return props.datasets;

    let summarizedData: ChartContainerDataSet[] = props.datasets.slice(0, 8);

    let lastSliceValue = 0;
    for (let i = 8; i < props.datasets.length; i++) {
      lastSliceValue =
        typeof props.datasets[i].data === "number"
          ? lastSliceValue + (props.datasets[i].data as number)
          : lastSliceValue;
    }

    summarizedData = [
      ...summarizedData,
      {
        data: lastSliceValue,
        label:
          props.remainingSumText ??
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.RemainingValues),
        isNotSelected: false,
      } as ChartContainerDataSet,
    ];
    return summarizedData;
  }

  function generateLegend(chart: Chart) {
    const legendClass = legendType === ChartEnums.LegendType.SMALL ? cssExports.smallLegend : "";

    const text = [];
    text.push(`<div class="${legendClass} ${cssExports.legend}"><ul class="${cssExports.legendUL}">`);

    const dataSet = chart.data.datasets ? chart.data.datasets[0] : chart.data.datasets;
    const color = dataSet?.backgroundColor;
    let colors: Chart.ChartColor[] = [];
    if (color !== undefined) {
      colors = color as ChartColor[];
    }

    // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
    if (dataSet?.data != null && dataSet?.data != undefined) {
      for (let i = 0; i < (dataSet.data.length ? dataSet?.data?.length : 0); i++) {
        const data = dataSet.data[i];
        const label = chart?.data?.labels ? chart?.data?.labels[i] : "";

        let pointerBackgroundColor = GraphConstants.DefaultPointerColor;
        if (i < colors.length && colors[i] !== undefined) {
          pointerBackgroundColor = colors[i].toString();
        }

        text.push(generateLegendItem(pointerBackgroundColor, data, label));
      }
    }

    text.push("</ul></div>");
    return text.join("");
  }

  function generateLegendItem(
    pointerBackgroundColor: string,
    data: number | number[] | Chart.ChartPoint | null | undefined,
    label: string | number | number[] | string[] | Date | Date[] | moment.Moment | moment.Moment[]
  ) {
    let text = `<li class="${cssExports.legendLi}">`;

    if (UtilsHelper.notUndefinedOrNull(data) && typeof data === "number") {
      text = `${text}<div class="${cssExports.legendIndicator}"><span style="background-color:${pointerBackgroundColor}"></span></div><div class="${cssExports.legendText}">${label}</div>`;

      if (legendType === ChartEnums.LegendType.WITH_VALUES) {
        text = `${text}<div class="${cssExports.amounts}"> <label>${LocalizationHelperInstance.getFormattedNumber(
          data,
          0,
          0
        )} ${props.dataUnit ?? GraphConstants.DefaultDataUnit}</label></div>`;
      }
    }

    text = `${text}</li>`;
    return text;
  }

  const configureLegendClick = (callback: (legendItems: HTMLLIElement) => void, chart?: Chart) => {
    const myLegendContainer = document.getElementById(chartLegendId);
    // generate HTML legend
    if (myLegendContainer != null && chart) myLegendContainer.innerHTML = chart.generateLegend() as string;

    const legendItems = myLegendContainer?.getElementsByTagName("li");
    // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
    if (legendItems != undefined && legendItems.length > 1 && dataPoints?.some((d) => !d.isNotSelected && !!d.data))
      for (let i = 0; i < legendItems.length; i += 1) {
        callback(legendItems[i]);
        if (dataPoints && dataPoints[i].isNotSelected && chart) {
          hideItem(chart, legendItems[i], i);
        }
      }
  };

  function legendClickCallback(this: HTMLLIElement, event: MouseEvent) {
    const parent = this?.parentElement;
    if (parent == null) return;
    const target = this;

    Chart.helpers.each(Chart.instances, function (instance: any) {
      if (instance.chart.canvas.id === chartCanvasId && parent !== null) {
        const chart = instance.chart;
        const index = Array.prototype.slice.call(parent.children).indexOf(target);

        hideItem(chart, target, index);
      }
    });
  }

  function hideItem(chart: any, target: HTMLLIElement, index: number) {
    const meta = chart.getDatasetMeta(0);
    const item = meta.data[index];

    if (item === undefined) return;

    if (item.hidden === undefined || item.hidden === false) {
      item.hidden = true;
      target.classList.add(cssExports.hiddenLegend);
    } else {
      target.classList.remove(cssExports.hiddenLegend);
      item.hidden = undefined;
    }

    chart.update();
  }

  return (
    <Grid container direction="row" className={clsx(className, cssExports.doughnutGraphContainer)} spacing={2}>
      <Grid item xs={6} className={cssExports.doughnutChartCanvasCell}>
        <div className={cssExports.doughnutCanvasBox}>
          <canvas id={chartCanvasId} />
        </div>
      </Grid>

      <Grid item container xs={6} alignContent="center">
        {props.title && (
          <Grid item container className={cssExports.chartTitle}>
            {props.title}
          </Grid>
        )}
        {props.showTotal && (
          <MultiLineChartTitle
            rows={[
              {
                title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InTotal),
                value: `${LocalizationHelperInstance.getFormattedNumber(total, 0, 2)} ${
                  props.dataUnit ?? GraphConstants.DefaultDataUnit
                }`,
              },
            ]}
          />
        )}
        {!props.hideLegend && <Grid item container id={chartLegendId}></Grid>}
      </Grid>
    </Grid>
  );
}
